import type { Situation } from "../types.js";
import { SIN_SENAL } from "./draft-situations.js";

/**
 * DOS SITUACIONES SOBRE CONTRADICCIONES — para arreglar la cobertura, no para
 * añadir contenido.
 *
 * CALIBRADAS por el autor el 18 de agosto de 2026: +2 las seis. La escala
 * reserva ese valor a «evidencia motivacional directa», y una contradicción
 * reconocida lo es por construcción — quien la admite está describiendo el
 * motivo, no la conducta.
 *
 * POR QUÉ EXISTEN. `npm run espacio` muestreó 200.000 recorridos al azar y
 * encontró que los nueve tipos NO tienen oportunidades comparables:
 *
 *     6: 10,6 %   9: 5,4 %   1: 4,3 %   5: 2,5 %   3: 1,1 %
 *     7:  0,8 %   4: 0,6 %   2: 0,5 %   8: 0,0 %  ← cero de 200.000
 *
 * El 8 era «alcanzable» en el mejor caso y no ocupaba superficie medible. La
 * causa no eran los pesos, era el reparto: las siete primeras situaciones se
 * diseñaron para el alfa 1-6-9, y los seis tipos nuevos recibieron una cada
 * uno. Los del alfa puntúan en las suyas Y en los distractores de las nuevas.
 *
 *     tipo   situaciones   techo          tipo   situaciones   techo
 *       6         5         11              7         3          4
 *       1         5          9              2         2          4
 *       9         4          7              4         2          4
 *       3         4          6              8         2          3
 *       5         3          6
 *
 * Estas dos dan una situación más al 8, al 2 y al 4 —los tres peores— y otra
 * al 7, al 5 y al 3. **No dan nada al 1, al 6 ni al 9**, a propósito: el
 * problema es la distancia entre ellos, y ensancharla más no la arregla.
 *
 * DE DÓNDE SALEN. Del campo «Contradicciones» del Bloque D, que estaba SIN
 * USAR en los nueve tipos. Es material distinto de las señales favorables: no
 * describe una conducta, describe **la distancia entre lo que alguien dice de
 * sí mismo y lo que hace** — que es exactamente la tesis del libro («the
 * distance between what you know and what you do») y la clase de evidencia más
 * difícil de falsear, porque quien la reconoce se está delatando.
 *
 * Cada una lleva su salida neutra: sin ella, la elección forzada obliga a
 * puntuar a alguien aunque la persona no se reconozca en ninguna.
 *
 * En inglés, con el original español al lado.
 */

const V = "draft-contradictions-0.1";

export const SITUATIONS_CONTRADICTIONS: Situation[] = [
  {
    id: "S17",
    prompt: "Something you've said about yourself doesn't quite match what you do. Which of these is closest?",
    context: "cómo te describes frente a cómo actúas",
    purpose: "Cobertura del 8, el 2 y el 4 — los tres tipos con menos oportunidades de puntuar.",
    required: true,
    version: V,
    options: [
      {
        id: "S17_a",
        // 8: «Decir "no me importa lo que piensen de mí" y reaccionar con
        //     intensidad ante cualquier cuestionamiento de autoridad»
        text: "I say I don't care what people think — and something goes up in me the moment someone questions a call I've made.",
        signals: ["dice no importarle la opinión ajena, reacciona al cuestionamiento"],
        evidenceTags: ["8:reacciona-al-control"],
        counterevidenceTags: [],
        // Decir que no importa la opinión ajena y saltar al primer cuestionamiento:
        // la contradicción delata la sensibilidad al control, no la indiferencia
        // declarada.
        patternWeights: { "8": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 8, «Contradicciones»."
      },
      {
        id: "S17_b",
        // 2: «Decir "no espero nada a cambio" y reaccionar con dolor o
        //     resentimiento cuando no hay reciprocidad»
        text: "I say I expect nothing back — and it lands harder than it should when nothing comes back.",
        signals: ["dice no esperar nada, duele la falta de reciprocidad"],
        evidenceTags: ["2:ayuda-no-solicitada"],
        counterevidenceTags: [],
        // Decir que no se espera nada y que duela la falta de reciprocidad: la
        // contradicción es la evidencia — si de verdad no se esperara, no dolería.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 2, «Contradicciones»."
      },
      {
        id: "S17_c",
        // 4: «Decir "no me importa ser como los demás" y evitar activamente
        //     contextos o prendas "corrientes"»
        text: "I say I don't mind being like everyone else — and I quietly avoid whatever feels too ordinary.",
        signals: ["dice no importarle ser corriente, evita lo corriente"],
        evidenceTags: ["4:teme-lo-corriente"],
        counterevidenceTags: [],
        // Decir que no importa ser corriente y evitar activamente lo corriente. El
        // acto contradice la declaración, y el acto es el dato.
        patternWeights: { "4": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 4, «Contradicciones»."
      },
      {
        id: "S17_d",
        text: "None of these. What I say about myself and what I do line up well enough.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra. No informa sobre ningún patrón — y es una respuesta " +
               "legítima, no una evasiva: hay gente cuya contradicción no está aquí."
      }
    ]
  },

  {
    id: "S18",
    prompt: "And another one, if any of these fits better:",
    context: "cómo te describes frente a cómo actúas",
    purpose: "Cobertura del 7, el 5 y el 3 — los tres siguientes por debajo.",
    required: true,
    version: V,
    options: [
      {
        id: "S18_a",
        // 7: «Decir "disfruto del presente" y tener la agenda saturada de
        //     planes futuros constantemente»
        text: "I say I enjoy the present — and my calendar is always full of what's next.",
        signals: ["dice disfrutar el presente, agenda saturada de futuro"],
        evidenceTags: ["7:busca-mejor-opcion"],
        counterevidenceTags: [],
        // Decir que se disfruta el presente con la agenda llena de futuro: la
        // contradicción muestra dónde está puesta la atención de verdad.
        patternWeights: { "7": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 7, «Contradicciones»."
      },
      {
        id: "S18_b",
        // 5: «Decir "no necesito a nadie" y dedicar mucho tiempo a estudiar
        //     cómo relacionarse mejor con otros»
        text: "I say I don't need anyone — and I've spent a lot of time reading about how people work.",
        signals: ["dice no necesitar a nadie, estudia cómo relacionarse"],
        evidenceTags: ["5:prepara-en-soledad"],
        counterevidenceTags: [],
        // Decir que no se necesita a nadie y haber estudiado mucho cómo funciona la
        // gente: el estudio delata el interés que la frase niega.
        patternWeights: { "5": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 5, «Contradicciones»."
      },
      {
        id: "S18_c",
        // 3: «Decir "no me importa lo que piensen" y ajustar visiblemente el
        //     relato de sí mismo/a según la audiencia»
        text: "I say I don't care what they think — and the version of me they get depends on who's in the room.",
        signals: ["dice no importarle la opinión, ajusta el relato a la audiencia"],
        evidenceTags: ["3:exigencia-decae-sin-audiencia"],
        counterevidenceTags: [],
        // Decir que no importa lo que piensen y ajustar el relato a quien escucha.
        // La audiencia manda, aunque la frase diga que no.
        patternWeights: { "3": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 3, «Contradicciones»."
      },
      {
        id: "S18_d",
        text: "None of these either.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  S19 — la tercera del 8, y la tercera del 2.
  //
  //  Con S17 el 8 pasó de no salir nunca a salir el 0,3 %: dejó de ser
  //  imposible y siguió siendo el último con diferencia. Su techo era 5 puntos
  //  contra los 11 del 6.
  //
  //  Sale del campo «Atención automática» del Bloque B, que el canon tiene para
  //  los nueve tipos y que no se había usado en ninguna situación. Es distinto
  //  de una conducta: es a dónde va la mirada ANTES de decidir nada — «hacia el
  //  control de la situación, hacia quién tiene el poder» en el 8, «hacia las
  //  necesidades de los demás, antes que las propias» en el 2.
  //
  //  El distractor puntúa al 2 a propósito. Era el segundo peor cubierto, así
  //  que una sola situación levanta a los dos sin tocar a los que van sobrados.
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: "S19",
    prompt: "You walk into a room where you don't know anyone. What does your attention do first, before you've decided anything?",
    context: "una sala, un grupo, una reunión",
    purpose: "Tercera cobertura del 8 —el tipo con menos oportunidades— y del 2.",
    required: true,
    version: V,
    options: [
      {
        id: "S19_a",
        // 8, Bloque B: «Hacia el control de la situación, hacia quién tiene el
        //    poder»
        text: "It goes to who's actually in charge here — not who's talking, who decides.",
        signals: ["atención al poder y al control de la situación"],
        evidenceTags: ["8:reacciona-al-control"],
        counterevidenceTags: [],
        // Mirar quién decide antes de decidir nada: atención al poder. Bloque B del
        // 8, y es motivo y no conducta — la mirada va ahí sola.
        patternWeights: { "8": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque B del 8, «Atención automática»."
      },
      {
        id: "S19_b",
        // 2, Bloque B: «Hacia las necesidades de los demás, antes que las
        //    propias». Mismo escaneo de la sala, otra cosa buscada.
        text: "It goes to whoever looks like they need something — left out, uncomfortable, on their own.",
        signals: ["atención a la necesidad ajena antes que a la propia"],
        evidenceTags: ["2:ayuda-no-solicitada"],
        counterevidenceTags: [],
        // El mismo escaneo de la sala buscando otra cosa: quién necesita algo.
        // Bloque B del 2.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque B del 2, «Atención automática». Distractor real: el mismo " +
               "escaneo de la sala, buscando otra cosa."
      },
      {
        id: "S19_c",
        // 8: «Qué la debilitaría» — no leer la sala en clave de poder. Es
        // contraevidencia del 8 sin ser evidencia de nadie: mucha gente entra
        // en una sala sin leer nada.
        text: "Nothing in particular. I'm not reading the room for any of that.",
        signals: ["no lee la sala en clave de poder"],
        evidenceTags: [],
        counterevidenceTags: ["8:cede-control-sin-derrota"],
        // No leer la sala en clave de poder contradice la atención automática del 8.
        // No puntúa a nadie más: entrar sin leerla es lo normal.
        patternWeights: { "8": -2 },
        confidence: "medium",
        sourceStatus: "inferencia",
        notes: "Contraevidencia del 8. No puntúa a nadie más a propósito: entrar en " +
               "una sala sin leerla no es señal de ningún patrón, es lo normal."
      },
      {
        id: "S19_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  }
];
